/**
 * 개발자 후원(건전지) API — 수익모델 §4 · SC-023.
 *
 * BE: DonationController @ /api/v1/user/donation (전부 인증 필요)
 *   GET  /batteries    내 건전지 수
 *   POST /batteries    영수증 검증 + 적립
 *
 * ⚠ **보상은 표식뿐이다.** 광고는 후원 뒤에도 그대로 켜져 있고 기능 차이도 없다
 *   (수익모델 §4.2 — "기능이든 광고든 인질로 잡는 순간 후원이 아니게 된다").
 *   그래서 이 API 는 권한을 만들지 않는다. 세는 것이 전부다.
 *
 * ⚠ **개수의 진짜 출처는 서버(계정 기준)다.** 소모성 IAP 는 복원이 안 돼서 기기에 두면
 *   앱을 지우는 순간 사라진다(오픈이슈 OI-10). `data/donation.ts` 의 sessionStorage 는
 *   결제가 없던 시절의 프로토타입 자리였고, 이제 여기가 정본이다.
 */
import { apiGet, apiPost } from "./client";

/** 결제가 일어난 스토어. BE StorePlatform 과 1:1. */
export type StorePlatform = "IOS" | "ANDROID";

export interface BatteryCount {
  batteryCount: number;
}

export interface VerifyResult {
  /** 지금 내 건전지 수 (수량 합 — 행 수가 아니다) */
  batteryCount: number;
  /**
   * 결제 보류(Google 느린 결제수단 등). 아직 안 세어졌다.
   * 결제가 확정되면 앱이 같은 영수증을 다시 보내고, 그때 세어진다.
   */
  pending: boolean;
  /**
   * 이미 처리된 영수증이었다.
   *
   * ⚠ **이건 에러가 아니다.** 서버가 커밋한 직후 응답이 유실되면 앱이 같은 영수증을
   *   다시 보내는데, 그걸 실패로 다루면 돈 낸 사용자가 실패 화면을 본다.
   *   화면은 duplicated 여부와 무관하게 성공으로 처리하면 된다.
   */
  duplicated: boolean;
}

/**
 * 영수증 검증 요청.
 *
 * 플랫폼별로 채워지는 필드가 다르다 — iOS 는 {@link transactionJws}, Android 는
 * {@link purchaseToken}. 서버가 어느 쪽이든 같은 형태로 정규화한다.
 */
export interface VerifyRequest {
  platform: StorePlatform;
  productId: string;
  /** iOS — StoreKit 2 서명 트랜잭션(JWS) */
  transactionJws?: string;
  /** Android — Play 구매 토큰 */
  purchaseToken?: string;
  /** Android — 로컬 영수증 JSON */
  receipt?: string;
  /** Android — 로컬 영수증 서명 */
  signature?: string;
}

/**
 * 후원 기능이 열려 있는가 — 앱이 진입점을 그릴지 말지의 유일한 근거.
 *
 * 값은 `GET /app/version-policy`(permitAll) 의 `donationEnabled` 다. OTA 응답에 얹혀 있는
 * 이유는 그게 앱이 부팅·복귀마다 부르는 유일한 공개 GET 이라서다(PLAN_BATTERY_IAP §11.3.1).
 *
 * ⚠ **필드가 없으면 false 로 읽는다.** 옛 BE 나 조회 실패 때 "열림" 으로 치면, 스토어 상품이
 *   없는 환경에서 결제 버튼을 그려 놓고 누르면 실패하는 상태가 된다 — 없는 편이 낫다.
 */
export async function fetchDonationEnabled(): Promise<boolean> {
  const policy = await apiGet<{ donationEnabled?: boolean } | null>("/app/version-policy");
  return policy?.donationEnabled === true;
}

export async function fetchBatteryCount(): Promise<BatteryCount> {
  return apiGet<BatteryCount>("/donation/batteries");
}

/**
 * 영수증을 서버에 검증시키고 적립한다.
 *
 * ⚠ **성공(200)을 받은 뒤에만 스토어 트랜잭션을 finish 해야 한다.** 먼저 finish 하면
 *   검증이 실패했을 때 되돌릴 방법이 없다 — 스토어는 이미 완료로 보고 다시 안 준다.
 *   반대로 finish 를 안 하면 스토어가 계속 재전달하므로, 실패는 안전한 쪽이다.
 */
export async function verifyPurchase(req: VerifyRequest): Promise<VerifyResult> {
  return apiPost<VerifyResult>("/donation/batteries", req);
}
